import { Link } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useAcknowledgeAlert } from "../../api/hooks/useAcknowledgeAlert";
import { useAlerts } from "../../api/hooks/useAlerts";
import type { components } from "../../api/schema";
import { cn } from "../../lib/cn";

type AlertResponse = components["schemas"]["AlertResponse"];

function formatFired(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return `${dt.toISOString().slice(0, 10)} ${dt.toISOString().slice(11, 16)}Z`;
}

// Top-right alerts bell. Replaces the former sidebar "Alerts" sub-nav group.
// The badge probes pending-only (acknowledged_at IS NULL) for a cheap unread
// count; the panel shows the most recent inbox entries for quick triage and
// links out to the full Inbox / Rules pages, which remain authoritative.
export function AlertsBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const pending = useAlerts({ include_acknowledged: false, limit: 500 });
  const pendingCount = pending.data?.items.length ?? 0;

  const recent = useAlerts({ include_acknowledged: true, limit: 20 });
  const recentItems = recent.data?.items ?? [];

  const acknowledge = useAcknowledgeAlert();

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          pendingCount > 0 ? `Alerts, ${pendingCount} unread` : "Alerts"
        }
        onClick={() => setOpen((prev) => !prev)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-control text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
      >
        <Bell aria-hidden="true" className="h-5 w-5" />
        {pendingCount > 0 ? (
          <span
            role="status"
            aria-hidden="true"
            data-testid="alerts-bell-badge"
            className="absolute -right-0.5 -top-0.5 inline-flex min-w-[1.125rem] items-center justify-center rounded-full bg-primary px-1 text-[0.625rem] font-medium leading-4 text-on-primary"
          >
            {pendingCount > 99 ? "99+" : pendingCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Alerts"
          className="absolute right-0 z-20 mt-2 w-80 overflow-hidden rounded-control border border-outline-variant bg-surface-container shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-outline-variant px-3 py-2">
            <span className="text-sm font-medium text-on-surface">Alerts</span>
            <Link
              to="/alerts/rules"
              onClick={() => setOpen(false)}
              className="text-xs font-medium text-primary underline"
            >
              Manage rules
            </Link>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {recent.isLoading ? (
              <p className="px-3 py-4 text-sm text-on-surface-variant">
                Loading alerts…
              </p>
            ) : recent.isError ? (
              <p
                role="alert"
                className="px-3 py-4 text-sm font-medium text-danger"
              >
                Failed to load alerts.
              </p>
            ) : recentItems.length === 0 ? (
              <p className="px-3 py-4 text-sm text-on-surface-variant">
                No alerts yet.
              </p>
            ) : (
              <ul
                data-testid="alerts-bell-list"
                className="divide-y divide-outline-variant"
              >
                {recentItems.map((alert: AlertResponse) => (
                  <li
                    key={alert.alert_id}
                    className={cn(
                      "px-3 py-2",
                      alert.is_acknowledged ? "opacity-60" : null,
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Link
                        to="/trades/$tradeId"
                        params={{ tradeId: alert.trade_id }}
                        search={{ from: "alerts" }}
                        onClick={() => setOpen(false)}
                        className="text-sm font-medium text-primary underline"
                      >
                        Trade {alert.trade_id.slice(0, 8)}
                      </Link>
                      {alert.is_acknowledged ? null : (
                        <button
                          type="button"
                          disabled={acknowledge.isPending}
                          onClick={() => acknowledge.mutate(alert.alert_id)}
                          className="rounded-control border border-outline-variant bg-surface px-2 py-0.5 text-xs text-on-surface transition-colors hover:bg-surface-container-high"
                        >
                          Mark seen
                        </button>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-on-surface-variant">
                      Fired {formatFired(alert.fired_at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-outline-variant px-3 py-2 text-right">
            <Link
              to="/alerts"
              onClick={() => setOpen(false)}
              className="text-xs font-medium text-primary underline"
            >
              View all alerts
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
